// InvestWidget.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio, TextField, InputAdornment,
  Grid, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, useMediaQuery, useTheme, CircularProgress, TableContainer, Table,
  TableHead, TableRow, TableCell, TableBody, Paper, Chip, LinearProgress,
  Stack, Divider, Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NumbersIcon from '@mui/icons-material/Numbers';

import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, doc, getDoc, setDoc, updateDoc, serverTimestamp, addDoc,
  writeBatch, getDocs, query, where
} from 'firebase/firestore';

const DAY_MS = 24 * 60 * 60 * 1000;





// === Loterías → entrada del API a comparar ===
const LOTTERY_TARGETS = {
  'Lotería Nacional Tarde': {
    description: 'Loteria Nacional 2:30 PM Gana Más',
    id: 35904,
    id_loterias: 2,
  },
  'Lotería Nacional Noche': {
    description: 'Loteria Nacional 8:50 PM',
    id: 35971,
    id_loterias: 3,
  },
  'Lotería Real': {
    description: 'Loto Real 12:55 PM',
    id: 35788,
    id_loterias: 1,
  },
  'Leidsa': {
    description: 'Quiniela Pale Leidsa 8:55 PM / Dom 3:00 PM',
    id: 35755,
    id_loterias: 6,
  },
};

// ⚠️ Cambia por tu proxy si necesitas evitar CORS
const API_URL = 'https://sorteosrd.com/api/sorteosrd-results/217XUR6ivy8A1NdI4SKcnAa9YGZRV7w3';

// Normaliza fechas a 'YYYY-MM-DD'
function toYMD(d) {
  const dt = (d instanceof Date) ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// Intenta extraer una fecha del objeto del API
function extractDateFromApiEntry(entry) {
  // Estas claves son suposiciones comunes; ajusta si tu API usa otro nombre
  const candidates = [
    entry.fecha,
    entry.date,
    entry.fecha_sorteo,
    entry.sorteo_fecha,
    entry.created_at,
    entry.updated_at,
  ].filter(Boolean);

  for (const c of candidates) {
    const d = new Date(c);
    if (!isNaN(d.getTime())) return toYMD(d);
  }

  // fallback: si no hay fecha, intentamos hoy
  return toYMD(new Date());
}

// Intenta leer num1/num2/num3 del objeto y normalizar
function normalizeNumField(v) {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  if (!s) return '';
  // si viniera con más de 2 dígitos, dejamos como está; si no, padStart(2)
  return s.length <= 2 ? s.padStart(2, '0') : s;
}

export default function InvestWidget() {
  const [period, setPeriod] = useState('7');
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [selectedNums, setSelectedNums] = useState([]);
  const [lottery, setLottery] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [refPercent, setRefPercent] = useState(5);


  // Sesión
  const [userKey, setUserKey] = useState(null);
  const [usesAuthUid, setUsesAuthUid] = useState(false);

  const [role, setRole] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loadingUser, setLoadingUser] = useState(true);

  // inversión activa / bloqueo
  const [checkingActive, setCheckingActive] = useState(true);
  const [activeInv, setActiveInv] = useState(null); // {id, amount, periodDays, startAtMs, endAtMs, ...}

  // “Reloj” para countdown (no reconstruye data pesada)
  const [nowMs, setNowMs] = useState(Date.now());

  // Calendario y totales
  const [lastInvestmentId, setLastInvestmentId] = useState(null);
  const [dailyGains, setDailyGains] = useState([]); // {day, dateISO, base, adjusted, accumulated, status, matched}
  const [totalGain, setTotalGain] = useState(0);

  // Cache resultados del API por lotería → Map<YYYY-MM-DD, {num1,num2,num3}>
  const [apiResultsByDate, setApiResultsByDate] = useState(new Map());

  // Evitar reconsultar API repetidamente el mismo día
  const [lastAppliedDayIdx, setLastAppliedDayIdx] = useState(-1);
  const prevDayIdxRef = useRef(-1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const showMessage = (msg) => setSnackbar({ open: true, message: msg });
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const toCurrency = (n) =>
    new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', maximumFractionDigits: 0 }).format(n || 0);

  const parsedAmount = useMemo(() => Number(amount || 0), [amount]);
  const periodNum = useMemo(() => Number(period || 1), [period]);




  useEffect(() => {
    const byRole = { normal: 5, oro: 7, moderador: 7, platino: 10 };
    setRefPercent(byRole[role] ?? 5);
  }, [role]);







  // --------- Detectar sesión ---------
  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = onAuthStateChanged(auth, (user) => {
        if (user) {
          setUserKey(user.uid);
          setUsesAuthUid(true);
        } else {
          const lsUser = (localStorage.getItem('username') || '').trim().toLowerCase();
          if (lsUser) {
            setUserKey(lsUser);
            setUsesAuthUid(false);
          } else {
            setUserKey(null);
            setUsesAuthUid(false);
          }
        }
      });
    } catch {
      const lsUser = (localStorage.getItem('username') || '').trim().toLowerCase();
      if (lsUser) {
        setUserKey(lsUser);
        setUsesAuthUid(false);
      }
    }
    return () => unsub && unsub();
  }, []);

  // --------- Cargar/asegurar documento de usuario ---------
  useEffect(() => {
    const loadUser = async () => {
      setLoadingUser(true);
      setRole(null);
      setBalance(0);
      try {
        if (!userKey) { setLoadingUser(false); return; }
        const userRef = doc(db, 'users', userKey);
        let snap = await getDoc(userRef);
        if (!snap.exists()) {
          await setDoc(userRef, {
            username: usesAuthUid ? null : userKey,
            role: 'normal',
            montoInvers: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }, { merge: true });
          snap = await getDoc(userRef);
        }
        const data = snap.data() || {};
        setRole(data.role || 'normal');
        if (typeof data.montoInvers !== 'number') {
          await updateDoc(userRef, { montoInvers: 0, updatedAt: serverTimestamp() });
          setBalance(0);
        } else {
          setBalance(data.montoInvers);
        }
      } catch (e) {
        console.error(e);
        showMessage('No se pudo cargar el usuario.');
      } finally {
        setLoadingUser(false);
      }
    };
    loadUser();
  }, [userKey, usesAuthUid]);

  // ====== Descarga y mapea resultados del API por FECHA (YYYY-MM-DD) ======
  // Se ejecuta cuando cambia la lotería seleccionada o al confirmar inversión.
  const fetchLotteryResultsMap = async (lotteryName) => {
    const target = LOTTERY_TARGETS[lotteryName];
    if (!target) return new Map();

    const res = await fetch(API_URL);
    const data = await res.json();
    if (!Array.isArray(data)) return new Map();

    // Filtramos por id (preferible) o por description
    const filtered = data.filter((it) => {
      const byId = target.id && Number(it.id) === Number(target.id);
      const byDesc = it.descripcion && String(it.descripcion).toLowerCase().includes(target.description.toLowerCase());
      return byId || byDesc;
    });

    // Armamos Map<fecha, {num1,num2,num3}>
    const map = new Map();
    for (const it of filtered) {
      const dateStr = extractDateFromApiEntry(it);
      const num1 = normalizeNumField(it.num1);
      const num2 = normalizeNumField(it.num2);
      const num3 = normalizeNumField(it.num3);
      // Si hay varias entradas por fecha, la última sobrescribe (ajusta si prefieres otra regla)
      map.set(dateStr, { num1, num2, num3 });
    }
    return map;
  };

  // Coincidencia SOLO en PRIMERA (num1)
  function isFirstPrizeMatchForDate(dateStr, resultsMap, selected) {
    if (!resultsMap || !resultsMap.size) return false;
    const entry = resultsMap.get(dateStr);
    if (!entry || !entry.num1) return false;
    const pick = new Set(selected.map(s => s.padStart(2, '0')));
    return pick.has(entry.num1.padStart(2, '0'));
  }

  // ========= Chequear inversión activa (solo al montar / cambio de usuario) =========
  useEffect(() => {
    const checkActive = async () => {
      setCheckingActive(true);
      setActiveInv(null);
      setDailyGains([]);
      setTotalGain(0);
      setApiResultsByDate(new Map());
      setLastAppliedDayIdx(-1);
      prevDayIdxRef.current = -1;

      try {
        if (!userKey) { setCheckingActive(false); return; }

        let snaps;
        try {
          const q1 = query(
            collection(db, 'investments'),
            where('userId', '==', userKey),
            where('status', '==', 'active')
          );
          snaps = await getDocs(q1);
        } catch {
          const q2 = query(collection(db, 'investments'), where('userId', '==', userKey));
          snaps = await getDocs(q2);
        }

        let found = null;
        for (const d of snaps.docs) {
          const inv = d.data() || {};
          if (inv.status !== 'active') continue;

          const startMs =
            inv.startAtMs ??
            (inv.createdAt?.toDate ? inv.createdAt.toDate().getTime() : Date.now());
          const endMs = inv.endAtMs ?? (startMs + Number(inv.periodDays || 0) * DAY_MS);
          found = { id: d.id, ...inv, startAtMs: startMs, endAtMs: endMs };
          break;
        }

        if (found) {
          setActiveInv(found);

          // Construye el calendario base (ajusted=0 por defecto; solo paga si hay acierto por fecha)
          const pDays = Number(found.periodDays || 0);
          const amt = Number(found.amount || 0);
          const baseDaily = Number((((amt / 100) * 20) / (pDays || 1)).toFixed(2));

          const rows = [];
          let acc = 0;
          for (let i = 0; i < pDays; i++) {
            const dayStart = found.startAtMs + i * DAY_MS;
            const dateStr = toYMD(new Date(dayStart));
            const liberated = Date.now() >= (found.startAtMs + (i + 1) * DAY_MS);
            const adjusted = 0;
            acc += adjusted;
            rows.push({
              day: i + 1,
              dateISO: dateStr,
              base: baseDaily,
              adjusted,
              accumulated: Number(acc.toFixed(2)),
              status: liberated ? 'Liberada' : 'Pendiente',
              matched: false,
            });
          }
          setDailyGains(rows);
          setLastInvestmentId(found.id);

          // Si ya hay lotería seleccionada (por UI) y números, descargamos resultados y aplicamos por fecha
          if (found.lottery || lottery) {
            const chosenLottery = lottery || found.lottery;
            const resultsMap = await fetchLotteryResultsMap(chosenLottery);
            setApiResultsByDate(resultsMap);

            // Aplica a todas las fechas del rango (no solo hoy)
            applyResultsByDateToRows(found, rows, resultsMap, selectedNums.length ? selectedNums : found.selectedNums || []);
          }
        }
      } catch (e) {
        console.error('Error verificando inversión activa:', e);
      } finally {
        setCheckingActive(false);
      }
    };

    checkActive();
    // No depende de nowMs para evitar refrescos constantes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  // Aplica resultados del API (por fecha) a todas las filas
  const applyResultsByDateToRows = (inv, rowsState, resultsMap, picks) => {
    if (!inv || !rowsState || !rowsState.length) return;

    const selected = (picks && picks.length ? picks : selectedNums).map(s => s.padStart(2, '0'));
    setDailyGains(() => {
      let acc = 0;
      const updated = rowsState.map((r, idx) => {
        const liberated = Date.now() >= (inv.startAtMs + (idx + 1) * DAY_MS);
        const match = isFirstPrizeMatchForDate(r.dateISO, resultsMap, selected);
        const adjusted = (liberated && match) ? Number((r.base * 4).toFixed(2)) : 0;
        acc += adjusted;
        return {
          ...r,
          status: liberated ? 'Liberada' : 'Pendiente',
          matched: liberated && match,
          adjusted,
          accumulated: Number(acc.toFixed(2)),
        };
      });
      const total = updated.reduce((s, r) => s + (r.adjusted || 0), 0);
      setTotalGain(Number(total.toFixed(2)));
      return updated;
    });
  };

  // Cuando cambie la lotería o los números seleccionados, actualizar mapa y reaplicar
  useEffect(() => {
    const refresh = async () => {
      if (!activeInv) return;
      if (!lottery || selectedNums.length === 0) return;

      const resultsMap = await fetchLotteryResultsMap(lottery);
      setApiResultsByDate(resultsMap);
      setLastAppliedDayIdx(-1); // para permitir re-aplicación del día actual si cambió algo

      setDailyGains((rows) => {
        // recalcula con el nuevo mapa
        const copy = rows.map(r => ({ ...r }));
        applyResultsByDateToRows(activeInv, copy, resultsMap, selectedNums);
        return copy;
      });
    };
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottery, JSON.stringify(selectedNums)]);

  // Observa cambios de día para actualizar estados Liberada/Pendiente y re-aplicar el día actual
  useEffect(() => {
    if (!activeInv) return;
    const dayIdx = Math.floor((Date.now() - activeInv.startAtMs) / DAY_MS);
    if (dayIdx !== prevDayIdxRef.current) {
      prevDayIdxRef.current = dayIdx;

      // Actualiza estados Liberada/Pendiente
      setDailyGains((rows) => rows.map((r, i) => ({
        ...r,
        status: Date.now() >= (activeInv.startAtMs + (i + 1) * DAY_MS) ? 'Liberada' : 'Pendiente'
      })));

      // Re-aplicar reglas del día con el mapa actual
      if (apiResultsByDate && apiResultsByDate.size) {
        setDailyGains((rows) => {
          const copy = rows.map(r => ({ ...r }));
          applyResultsByDateToRows(activeInv, copy, apiResultsByDate, selectedNums.length ? selectedNums : (activeInv.selectedNums || []));
          return copy;
        });
      }
      setLastAppliedDayIdx(dayIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowMs]);

  // Backfill opcional para montoInvers
  useEffect(() => {
    const runBackfillOnce = async () => {
      try {
        const roles = ['normal', 'oro', 'moderador', 'platino'];
        const qRef = query(collection(db, 'users'), where('role', 'in', roles));
        const snaps = await getDocs(qRef);
        const batch = writeBatch(db);
        let count = 0;
        snaps.forEach((d) => {
          if (count >= 200) return;
          const data = d.data() || {};
          if (typeof data.montoInvers !== 'number') {
            batch.update(doc(db, 'users', d.id), { montoInvers: 0, updatedAt: serverTimestamp() });
            count++;
          }
        });
        if (count > 0) await batch.commit();
      } catch (e) {
        console.warn('Backfill montoInvers (opcional):', e);
      }
    };
    runBackfillOnce();
  }, []);

  // Precargar input con balance
  useEffect(() => {
    if (!loadingUser) setAmount(String(balance || ''));
  }, [loadingUser, balance]);

  // % por rol
  useEffect(() => {
    const byRole = { normal: 5, oro: 7, moderador: 7, platino: 10 };
    setRefPercent(byRole[role] ?? 5);
  }, [role]);

  // --------- Helpers UI ---------
  const fmtCountdown = (ms) => {
    if (!ms || ms <= 0) return '0d 0h 0m 0s';
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${d}d ${h}h ${m}m ${sec}s`;
  };

  const investingLocked = !!activeInv;
  const remainingMs = investingLocked ? Math.max(0, (activeInv.endAtMs || 0) - nowMs) : 0;
  const canCredit = investingLocked && nowMs >= (activeInv?.endAtMs || 0);

  // --------- UI Handlers ---------
  const toggleNum = (num) => {
    setSelectedNums((prev) => {
      const exists = prev.includes(num);
      if (exists) return prev.filter((n) => n !== num);
      if (prev.length >= 5) {
        showMessage('Solo puede seleccionar 5 números');
        return prev;
      }
      const next = [...prev, num];
      if (next.length === 5) showMessage('Ya seleccionaste 5 números');
      return next;
    });
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;
    setAmount(val);
    setAmountError('');
  };

  const validateAmount = () => {
    if (amount === '') return true;
    const num = Number(amount);
    if (num < 500 || num > 5000) {
      setAmountError('El monto debe estar entre $500 y $5000');
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    if (!userKey) { showMessage('Debes iniciar sesión para invertir.'); return; }
    if (investingLocked) {
      showMessage('Ya tienes una inversión en curso. Debe finalizar y acreditarse para poder invertir de nuevo.');
      return;
    }
    if (!validateAmount()) return;
    if (selectedNums.length !== 5) { showMessage('Seleccione exactamente 5 números'); return; }
    if (!lottery) { showMessage('Seleccione una lotería'); return; }
    if (parsedAmount > balance) { showMessage('Saldo insuficiente. Reduce el monto o recarga tu monedero.'); return; }
    setSummaryOpen(true);
  };

  const handleCancel = () => setSummaryOpen(false);

  // --------- Confirmar inversión ---------
  const handleFinalConfirm = async () => {
    try {
      if (!userKey) { showMessage('Debes iniciar sesión para invertir.'); return; }
      if (investingLocked) { showMessage('Ya tienes una inversión activa.'); setSummaryOpen(false); return; }

      // Releer saldo
      const userRef = doc(db, 'users', userKey);
      const snap = await getDoc(userRef);
      const data = snap.data() || {};
      const currentBalance = typeof data.montoInvers === 'number' ? data.montoInvers : 0;

      if (parsedAmount > currentBalance) {
        showMessage('Saldo insuficiente al confirmar. Intenta nuevamente.');
        setSummaryOpen(false);
        return;
      }

      // 1) Debitar
      await updateDoc(userRef, {
        montoInvers: currentBalance - parsedAmount,
        updatedAt: serverTimestamp(),
      });

      // 2) Crear inversión
      const investmentsRef = collection(db, 'investments');
      const storedRef = localStorage.getItem('referrerId') || null;

      const startAtMs = Date.now();
      const endAtMs = startAtMs + periodNum * DAY_MS;

      const invDoc = await addDoc(investmentsRef, {
        userId: userKey,
        idType: usesAuthUid ? 'uid' : 'username',
        role: role || 'normal',
        lottery,
        selectedNums,
        amount: parsedAmount,
        periodDays: periodNum,
        possibleGains: parsedAmount * 75,
        startAtMs,
        endAtMs,
        startAtIso: new Date(startAtMs).toISOString(),
        endAtIso: new Date(endAtMs).toISOString(),
        createdAt: serverTimestamp(),
        status: 'active',
        referrerId: storedRef,
        refPercent: refPercent,
      });

      // 3) Construir calendario base (ajusted=0 por defecto; se pagará con API por fecha)
      const baseDaily = Number((((parsedAmount / 100) * 20) / periodNum).toFixed(2));
      const rows = [];
      let acc = 0;
      const now = Date.now();
      for (let i = 0; i < periodNum; i++) {
        const dayStart = startAtMs + i * DAY_MS;
        const dateStr = toYMD(new Date(dayStart));
        const liberated = now >= (startAtMs + (i + 1) * DAY_MS);
        const adjusted = 0;
        acc += adjusted;
        rows.push({
          day: i + 1,
          dateISO: dateStr,
          base: baseDaily,
          adjusted,
          accumulated: Number(acc.toFixed(2)),
          status: liberated ? 'Liberada' : 'Pendiente',
          matched: false,
        });
      }
      setDailyGains(rows);
      setTotalGain(0);
      setLastAppliedDayIdx(-1);
      setLastInvestmentId(invDoc.id);
      setSummaryOpen(false);
      showMessage('Inversión registrada correctamente');
      setBalance((b) => Math.max(0, (b || 0) - parsedAmount));
      const inv = { id: invDoc.id, amount: parsedAmount, periodDays: periodNum, startAtMs, endAtMs, status: 'active', lottery, selectedNums };
      setActiveInv(inv);

      // 4) Descargar resultados por fecha y aplicar a todas las fechas
      const resultsMap = await fetchLotteryResultsMap(lottery);
      setApiResultsByDate(resultsMap);
      applyResultsByDateToRows(inv, rows, resultsMap, selectedNums);
    } catch (e) {
      console.error(e);
      showMessage('Error al registrar la inversión.');
    }
  };

  // --------- Acreditar ganancia al finalizar período ---------
  const handleAcreditarGanancia = async () => {
    try {
      if (!userKey || !activeInv) return;
      if (nowMs < activeInv.endAtMs) {
        showMessage('Aún no finaliza el período de inversión.');
        return;
      }

      // 1) Acreditar al monedero
      const userRef = doc(db, 'users', userKey);
      const uSnap = await getDoc(userRef);
      const uData = uSnap.data() || {};
      const currentBalance = typeof uData.montoInvers === 'number' ? uData.montoInvers : 0;

      await updateDoc(userRef, {
        montoInvers: currentBalance + totalGain,
        updatedAt: serverTimestamp()
      });

      // 2) Marcar inversión como "completed"
      await updateDoc(doc(db, 'investments', activeInv.id), {
        status: 'completed',
        creditedAt: serverTimestamp(),
        creditedAmount: totalGain
      });

      // 3) Reset UI para permitir nueva inversión
      setActiveInv(null);
      setDailyGains([]);
      setTotalGain(0);
      setApiResultsByDate(new Map());
      setLastAppliedDayIdx(-1);
      prevDayIdxRef.current = -1;
      showMessage(`Ganancia de ${toCurrency(totalGain)} acreditada a tu balance. Ya puedes invertir nuevamente.`);
    } catch (e) {
      console.error(e);
      showMessage('Error al acreditar ganancia.');
    }
  };

  const numSize = isMobile ? 38 : 44;

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 700, mx: 'auto' }}>
      <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" textAlign="center" gutterBottom>
        Invierte y genera ganancia extra
      </Typography>

      {/* Banner de estado de inversión */}
      {(checkingActive || investingLocked) && (
        <Box sx={{ mb: 2 }}>
          {checkingActive ? (
            <Alert severity="info" icon={<CircularProgress size={16} />}>
              Verificando estado de tu inversión…
            </Alert>
          ) : investingLocked ? (
            <Alert severity={canCredit ? 'success' : 'warning'}>
              {canCredit ? (
                <>Tu inversión ya <strong>finalizó</strong>. Puedes acreditar tu ganancia total y volver a invertir.</>
              ) : (
                <>
                  Ya tienes una inversión en curso. Podrás invertir nuevamente cuando finalice.
                  <br />
                  <strong>Tiempo restante:</strong> {fmtCountdown(remainingMs)}
                </>
              )}
            </Alert>
          ) : null}
        </Box>
      )}

      {/* Label Personas invirtiendo */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2,
          background: (t) => (t.palette.mode === 'light' ? 'rgba(76,175,80,0.1)' : 'rgba(76,175,80,0.2)'),
          borderRadius: 2, py: 1, px: 2
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
          Personas invirtiendo en este momento: <span style={{ fontSize: 18, fontWeight: 800 }}>6</span>
        </Typography>
      </Box>

      <Typography variant="h6" fontWeight="bold" color="primary" textAlign="center" sx={{ mb: 1 }}>
        Selecciona la modalidad en que quieres invertir
      </Typography>

      <RadioGroup
        row value={period} onChange={(e) => setPeriod(e.target.value)}
        sx={{ justifyContent: 'center', mb: 3 }}
      >
        <FormControlLabel value="7" control={<Radio />} label="7 días" disabled={investingLocked} />
        <FormControlLabel value="30" control={<Radio />} label="30 días" disabled={investingLocked} />
      </RadioGroup>

      <Box sx={{ maxWidth: 340, mx: 'auto', mb: 3, width: '100%' }}>
        <TextField
          fullWidth label="Monto a invertir" value={amount}
          onChange={handleAmountChange}
          onBlur={validateAmount}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), validateAmount())}
          disabled={investingLocked}
          error={!!amountError}
          helperText={
            amountError ||
            (loadingUser ? 'Cargando balance…'
              : userKey ? `Balance disponible: ${toCurrency(balance)}`
              : 'Inicia sesión para ver tu balance')
          }
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: !loadingUser ? (
              <InputAdornment position="end" sx={{ fontSize: 12 }}>{toCurrency(balance)}</InputAdornment>
            ) : (
              <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
            ),
          }}
          inputProps={{ inputMode: 'numeric' }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2, backgroundColor: '#f5f5f5',
              '& fieldset': { borderColor: '#ccc' },
              '&:hover fieldset': { borderColor: '#999' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
            },
            '& .MuiInputLabel-root': { color: 'text.secondary' },
            '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' }
          }}
        />
        {!loadingUser && balance > 0 && (
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Button size="small" onClick={() => setAmount(String(balance))} disabled={investingLocked}>
              Usar todo el balance
            </Button>
          </Box>
        )}
      </Box>

      {/* Números 00-99 */}
      <Grid container spacing={isMobile ? 1 : 1.5} sx={{ maxWidth: isMobile ? 220 : 360, mx: 'auto', mb: 2 }}>
        {Array.from({ length: 100 }).map((_, i) => {
          const num = i.toString().padStart(2, '0');
          const isSel = selectedNums.includes(num);
          return (
            <Grid item xs={3} sm={2} md={1} key={num}>
              <Box
                onClick={() => !investingLocked && toggleNum(num)}
                onKeyDown={(e) => !investingLocked && (e.key === 'Enter' || e.key === ' ') && toggleNum(num)}
                role="button" tabIndex={0} aria-pressed={isSel} aria-disabled={investingLocked}
                sx={{
                  cursor: investingLocked ? 'not-allowed' : 'pointer',
                  width: numSize, height: numSize, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
                  borderRadius: 1.5, border: '1px solid #ccc',
                  bgcolor: isSel ? 'primary.main' : `hsl(${i * 3.6}, 70%, 90%)`,
                  color: isSel ? 'white' : 'text.primary', userSelect: 'none',
                  fontSize: isMobile ? 16 : 18, transition: 'background 0.2s, transform 0.1s',
                  '&:active': { transform: investingLocked ? 'none' : 'scale(0.98)' },
                  outline: 'none', '&:focus-visible': { boxShadow: '0 0 0 3px rgba(25,118,210,0.35)' },
                  mx: 'auto', opacity: investingLocked ? 0.6 : 1
                }}
              >
                {num}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Typography
          variant="h6" fontWeight="bold" color="primary.main"
          sx={{
            letterSpacing: 0.5,
            background: theme.palette.mode === 'light' ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.18)',
            borderRadius: 2, py: 1, px: 2, display: 'inline-block', textAlign: 'center'
          }}
        >
          Seleccionados:&nbsp;
          <span style={{ fontWeight: 800 }}>{selectedNums.join(', ') || '—'}</span>
          &nbsp;<span style={{ fontWeight: 400, fontSize: 14 }}>(máx. 5)</span>
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 300, mx: 'auto', mb: 3, width: '100%' }}>
        <Select
          fullWidth value={lottery} displayEmpty onChange={(e) => setLottery(e.target.value)}
          size={isMobile ? 'small' : 'medium'} disabled={investingLocked}
        >
          <MenuItem value="" disabled>Seleccione lotería</MenuItem>
          <MenuItem value="Lotería Nacional Tarde">Lotería Nacional Tarde</MenuItem>
          <MenuItem value="Lotería Nacional Noche">Lotería Nacional Noche</MenuItem>
          <MenuItem value="Lotería Real">Lotería Real</MenuItem>
          <MenuItem value="Leidsa">Leidsa</MenuItem>
        </Select>
      </Box>

      <Box textAlign="center">
        <Button
          variant="contained" size={isMobile ? 'medium' : 'large'}
          disabled={!amount || !!amountError || loadingUser || investingLocked}
          onClick={handleConfirm}
          sx={{ px: 4, width: isMobile ? '100%' : 'auto', mb: 2 }}
        >
          Revisar inversión
        </Button>

        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, justifyContent: 'center', mt: 1 }}>
          <Button variant="outlined" color="primary" size={isMobile ? 'medium' : 'large'} onClick={() => navigate('/coin-history')} sx={{ width: isMobile ? '100%' : 180 }}>
            Monedero
          </Button>
          <Button variant="outlined" color="secondary" size={isMobile ? 'medium' : 'large'} onClick={() => navigate('/chat')} sx={{ width: isMobile ? '100%' : 180 }}>
            Volver al chat
          </Button>
        </Box>
      </Box>

      {/* Resumen */}
      <Dialog open={summaryOpen} onClose={handleCancel} fullWidth maxWidth={isMobile ? 'xs' : 'sm'}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: '#1565c0', pb: 1 }}>
          Resumen de tu inversión
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NumbersIcon color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Números elegidos:</Typography>
              <Typography variant="subtitle1">{selectedNums.join(', ')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOnIcon color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Monto invertido:</Typography>
              <Typography variant="subtitle1" sx={{ color: '#388e3c', fontWeight: 700 }}>
                {toCurrency(parsedAmount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Posibles ganancias (juego):</Typography>
              <Typography variant="subtitle1" sx={{ color: '#1565c0', fontWeight: 700 }}>
                {toCurrency(parsedAmount * 75)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ganancia base diaria (20% / días):</Typography>
              <Typography variant="subtitle2">
                {toCurrency(Number((((parsedAmount / 100) * 20) / periodNum).toFixed(2)))}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              * Solo se paga el día si aciertas <strong>en primera</strong> (num1 del sorteo) uno de tus 5 números, en la misma fecha. El pago es base × 4.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pt: 1, px: 3 }}>
          <Button onClick={handleCancel} variant="outlined" color="error">Cancelar</Button>
          <Button variant="contained" onClick={handleFinalConfirm} color="success">Confirmar inversión</Button>
        </DialogActions>
      </Dialog>

      {/* Calendario (muestra ganancia diaria y total acumulado) */}
      {dailyGains.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            Calendario de ganancias (Inversión #{lastInvestmentId?.slice(0, 6)}…)
          </Typography>

          {isMobile ? (
            <Stack spacing={1.5}>
              {dailyGains.map((r) => (
                <Paper key={r.day} elevation={0} sx={{
                  p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                  display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'center'
                }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{`Día ${r.day} • ${r.dateISO}`}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        {r.matched ? <>Ganancia del día: <strong>{toCurrency(r.adjusted)}</strong></> : 'Sin acierto'}
                      </Typography>
                      <Typography variant="body2">Acum.: <strong>{toCurrency(r.accumulated)}</strong></Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={r.status === 'Liberada' ? 100 : 0}
                      sx={{ mt: 1, height: 8, borderRadius: 999 }}
                    />
                  </Box>
                  <Box sx={{ justifySelf: 'end', textAlign: 'right' }}>
                    <Chip
                      label={r.matched ? 'Acierto 1ra' : r.status}
                      size="small"
                      color={r.matched ? 'success' : (r.status === 'Liberada' ? 'default' : 'warning')}
                      variant={r.matched ? 'filled' : 'outlined'}
                    />
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} elevation={0}
              sx={{
                border: '1px solid #eee', borderRadius: 2, maxHeight: 420, overflow: 'auto',
                '&::-webkit-scrollbar': { height: 8, width: 8 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 8 }
              }}>
              <Table size="small" stickyHeader aria-label="Calendario de ganancias">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: (t) =>
                    t.palette.mode === 'light' ? 'rgba(25,118,210,0.06)' : 'rgba(25,118,210,0.16)' } }}>
                    <TableCell>Día</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Ganancia del día</TableCell>
                    <TableCell align="right">Acumulado</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{
                  '& tr:nth-of-type(odd)': { backgroundColor: (t) =>
                    t.palette.mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.025)' },
                  '& tr:hover': { backgroundColor: (t) =>
                    t.palette.mode === 'light' ? 'rgba(25,118,210,0.06)' : 'rgba(25,118,210,0.18)',
                    transition: 'background 0.15s' }
                }}>
                  {dailyGains.map((r) => (
                    <TableRow key={r.day}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.day}</TableCell>
                      <TableCell>{r.dateISO}</TableCell>
                      <TableCell align="right">{r.matched ? toCurrency(r.adjusted) : '—'}</TableCell>
                      <TableCell align="right">{toCurrency(r.accumulated)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={r.matched ? 'Acierto 1ra' : r.status}
                          size="small"
                          color={r.matched ? 'success' : (r.status === 'Liberada' ? 'default' : 'warning')}
                          variant={r.matched ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700, textAlign: 'center' }}>
            Ganancia total al finalizar: {toCurrency(totalGain)}
          </Typography>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="success"
              disabled={!canCredit}
              onClick={handleAcreditarGanancia}
            >
              Acreditar Ganancia
            </Button>
            {!canCredit && investingLocked && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Disponible cuando termine el período. Tiempo restante: {fmtCountdown(remainingMs)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />
      <Box sx={{ mt: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Ganancia por referidos
        </Typography>
        {/* Si deseas, agrega aquí tu render de referidos */}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={closeSnackbar} message={snackbar.message} />
    </Box>
  );
}
